import { Trans } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsUp, faThumbsDown } from "@fortawesome/pro-solid-svg-icons";
import Markdown from "react-markdown";
import "../utils/chatbotMarkDown.css";
import { ChatMessage } from "../models/chatbotModels";
import { ChatbotStateContext } from "../utils/chatbotContext";
import { useWebSocketContext } from "../utils/chatbotWSContext";

export const ChatbotResponse = ({
  message,
  openSourceDocumentModal,
}: {
  message: ChatMessage
  openSourceDocumentModal: (sourceDocument : string) => void;
}) => {
  const chatbotCurrentState = useContext(ChatbotStateContext);
  const { transferToZendesk, submitFeedback } = useWebSocketContext();
  const [feedback, setFeedback] = useState<string | null>(null);

  const handleZenDeskHandoff = () => {
    transferToZendesk(chatbotCurrentState.selectedBrand);
  };

  const handleFeedback = (thumbsUp: boolean) => {
    if (thumbsUp) {
      setFeedback("Positive");
    } else {
      setFeedback("Negative");
    }
    submitFeedback(message.message_id, thumbsUp);
  };

  useEffect(() => {
    if (message.feedback === "Positive") {
      setFeedback("Positive");
    } else if (message.feedback === "Negative") {
      setFeedback("Negative");
    } else {
      setFeedback(null);
    }
  }, [message]);

  return (
    <div className="flex w-full p-4">
      <div className="w-16 h-16 flex-shrink-0">
        <img
          src="/UB-Wingman-AI-Icon-No-Wings.png"
          alt="Wingman Avatar"
          className="w-full h-full rounded-full"
        />
      </div>
      <div className="pl-4 flex-grow">
        <p className="font-bold font-unleashed ml-2">
          <Trans>Wingman</Trans>
        </p>
        <div className="markdown-container">
          <Markdown>{message.message_text}</Markdown>
        </div>

        {message.source_docs && message.source_docs.length > 0 && (
          <div className="mt-2 ml-2">
            <span className="font-semibold">
              <Trans>Sources:</Trans>
            </span>
            {message.source_docs?.map((source, index) => (
              <span
                role="button"
                tabIndex={0}
                className="underline text-gray-500 ml-1"
                onKeyDown={(e) => e.key === "Enter" && handleZenDeskHandoff()}
                onClick={() => openSourceDocumentModal(source)}
                key={index}
              >
                {source}
                {index < (message.source_docs?.length ?? 0) - 1 ? ", " : ""}
              </span>
            ))}
          </div>
        )}
        <div className="mt-2 ml-2">
          <span className="font-semibold">
            <Trans>Zendesk:</Trans>
          </span>
          <span
            role="button"
            tabIndex={0}
            className="underline text-gray-500 ml-1"
            title="Zendesk"
            onKeyDown={(e) => e.key === "Enter" && handleZenDeskHandoff()}
            onClick={() => handleZenDeskHandoff()}
          >
            <Trans>Hand Off</Trans>
          </span>
        </div>

        <div className="flex space-x-2 mt-2 ml-2">
          <button
            onClick={() => handleFeedback(true)}
            className="active:transform active:translate-y-[-2px]"
          >
            <FontAwesomeIcon
              icon={faThumbsUp}
              className={`${
                feedback === "Positive" ? "text-[#13CEFD]" : "text-gray-300"
              }`}
            />
          </button>
          <button
            onClick={() => handleFeedback(false)}
            className="active:transform active:translate-y-[2px]"
          >
            <FontAwesomeIcon
              icon={faThumbsDown}
              className={`${
                feedback === "Negative" ? "text-[#13CEFD]" : "text-gray-300"
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

